import { privateApi } from '@/config/axios.config';
import {
  ConversationFilter,
  ConversationListResponse,
  Conversation,
  ConversationMessage,
  InboxStats,
} from '../types/inbox-types';

const BASE = '/inbox';

/** Lead vinculado a una conversación (subset renderizado en el panel de contexto). */
export interface InboxLinkedLead {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  stage?: string;
  status?: string;
  source?: string;
  budget?: number | string;
  createdAt?: string;
}

/** Respuesta rápida (quick reply): el selector del composer y la pantalla donde se administran. */
export interface QuickReply {
  id: string;
  name: string;
  content: string;
  category?: string;
  channels?: string[];
  /** Visible para todo el tenant; si no, solo para quien la creó. */
  isShared?: boolean;
  /** Quien la creó: es el único que puede editarla o borrarla. */
  createdByUserId?: string;
}

/** Lo que se manda al crear o editar una respuesta rápida. */
export interface QuickReplyInput {
  name: string;
  content: string;
  channels: string[];
  isShared: boolean;
}

/** Agente asignable a una conversación (los que tienen cuenta de usuario). */
export interface AssignableAgent {
  id: string;
  userId?: string;
  firstName: string;
  lastName: string;
}

/** Interacción/actividad de un lead (subset para el feed del panel de contexto). */
export interface LeadInteraction {
  id: string;
  type?: string;
  channel?: string;
  notes?: string;
  createdAt?: string;
  user?: { firstName?: string; lastName?: string } | null;
}

export const inboxApi = {
  getConversations: async (filters: ConversationFilter): Promise<ConversationListResponse> => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.channels?.length) {
      filters.channels.forEach((ch) => params.append('channels', ch));
    }
    if (filters.statuses?.length) {
      filters.statuses.forEach((st) => params.append('statuses', st));
    }
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.assignedToId) params.append('assignedToId', filters.assignedToId);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const { data } = await privateApi.get(`${BASE}/conversations?${params.toString()}`);
    return data?.data ?? data;
  },

  getConversationById: async (id: string): Promise<Conversation> => {
    const { data } = await privateApi.get(`${BASE}/conversations/${id}`);
    return data?.data ?? data;
  },

  /**
   * FIX CRÍTICO 2026-07-11 — pedimos `order=DESC` (los N MÁS RECIENTES) y los
   * revertimos para pintarlos en orden cronológico.
   *
   * Antes: el backend devolvía ASC + paginado ⇒ la página 1 traía los mensajes
   * MÁS VIEJOS. En un hilo largo el agente NO veía lo último que escribió el
   * cliente. Ahora el último mensaje del thread es, de verdad, el último.
   */
  getMessages: async (
    conversationId: string,
    page = 1,
    limit = 50,
  ): Promise<{ messages: ConversationMessage[]; total: number }> => {
    const { data } = await privateApi.get(
      `${BASE}/conversations/${conversationId}/messages?page=${page}&limit=${limit}&order=DESC`,
    );
    const payload = data?.data ?? data;
    return {
      ...payload,
      messages: [...(payload?.messages ?? [])].reverse(),
    };
  },

  createConversation: async (dto: {
    contactName: string;
    contactEmail?: string;
    contactPhone?: string;
    channel: string;
    subject?: string;
    assignedToId?: string;
    initialMessage?: string;
  }): Promise<Conversation> => {
    const { data } = await privateApi.post(`${BASE}/conversations`, dto);
    return data?.data ?? data;
  },

  sendMessage: async (
    conversationId: string,
    dto: { content: string; contentType?: string; direction?: string; attachments?: any[] },
  ): Promise<ConversationMessage> => {
    const { data } = await privateApi.post(
      `${BASE}/conversations/${conversationId}/messages`,
      dto,
    );
    return data?.data ?? data;
  },

  updateStatus: async (
    conversationId: string,
    status: string,
  ): Promise<Conversation> => {
    const { data } = await privateApi.patch(
      `${BASE}/conversations/${conversationId}/status`,
      { status },
    );
    return data?.data ?? data;
  },

  assignConversation: async (
    conversationId: string,
    assignedToId: string | null,
  ): Promise<Conversation> => {
    const { data } = await privateApi.patch(
      `${BASE}/conversations/${conversationId}/assign`,
      { assignedToId },
    );
    return data?.data ?? data;
  },

  markAsRead: async (conversationId: string): Promise<void> => {
    await privateApi.patch(`${BASE}/conversations/${conversationId}/read`);
  },

  getStats: async (): Promise<InboxStats> => {
    const { data } = await privateApi.get(`${BASE}/stats`);
    return data?.data ?? data;
  },

  // ── Lead linking (OLA1 GHL #3/#7) — panel de contexto + acciones ──
  getLinkedLead: async (conversationId: string): Promise<InboxLinkedLead | null> => {
    const { data } = await privateApi.get(`${BASE}/conversations/${conversationId}/lead`);
    return data?.data ?? data ?? null;
  },

  getLeadConversations: async (leadId: string): Promise<Conversation[]> => {
    const { data } = await privateApi.get(`${BASE}/leads/${leadId}/conversations`);
    return data?.data ?? data ?? [];
  },

  linkLead: async (conversationId: string, leadId: string): Promise<Conversation> => {
    const { data } = await privateApi.post(`${BASE}/conversations/${conversationId}/link`, { leadId });
    return data?.data ?? data;
  },

  unlinkLead: async (conversationId: string): Promise<Conversation> => {
    const { data } = await privateApi.delete(`${BASE}/conversations/${conversationId}/link`);
    return data?.data ?? data;
  },

  createLeadFromConversation: async (
    conversationId: string,
    source?: string,
  ): Promise<{ lead: InboxLinkedLead; conversation: Conversation }> => {
    const { data } = await privateApi.post(
      `${BASE}/conversations/${conversationId}/create-lead`,
      source ? { source } : {},
    );
    return data?.data ?? data;
  },

  // Mover la etapa del lead desde el inbox (acción GHL-signature). PUT /crm/leads/:id.
  updateLeadStage: async (leadId: string, stage: string): Promise<InboxLinkedLead> => {
    const { data } = await privateApi.put(`/crm/leads/${leadId}`, { stage });
    return data?.data ?? data;
  },

  // ── Quick replies (OLA1 GHL #4) — selector en el composer ──
  getQuickReplies: async (): Promise<QuickReply[]> => {
    const { data } = await privateApi.get(`${BASE}/quick-replies`);
    return data?.data ?? data ?? [];
  },

  // GH#2138 (GHL #4 firmado) — administrar las respuestas rápidas desde la pantalla.
  // Editar y borrar es solo del creador (el backend responde 403 a los demás).
  createQuickReply: async (input: QuickReplyInput): Promise<QuickReply> => {
    const { data } = await privateApi.post(`${BASE}/quick-replies`, input);
    return data?.data ?? data;
  },

  updateQuickReply: async (id: string, input: Partial<QuickReplyInput>): Promise<QuickReply> => {
    const { data } = await privateApi.patch(`${BASE}/quick-replies/${id}`, input);
    return data?.data ?? data;
  },

  deleteQuickReply: async (id: string): Promise<void> => {
    await privateApi.delete(`${BASE}/quick-replies/${id}`);
  },

  // ── Actividad / notas del lead desde el inbox (OLA1 GHL #3/#7 acción inline) ──
  // Registrar y ver actividad sin salir de la conversación (firma GHL). El backend
  // (POST /crm/interactions) exige userId NOT NULL → mandar el del user logueado;
  // type/channel = 'other' porque el enum no tiene valor "note".
  getLeadInteractions: async (leadId: string): Promise<LeadInteraction[]> => {
    const { data } = await privateApi.get(`/crm/interactions/lead/${leadId}`);
    return data?.data ?? data ?? [];
  },

  createLeadNote: async (
    leadId: string,
    userId: string,
    notes: string,
  ): Promise<LeadInteraction> => {
    const { data } = await privateApi.post(`/crm/interactions`, {
      leadId,
      userId,
      type: 'other',
      channel: 'other',
      notes,
    });
    return data?.data ?? data;
  },

  // ── Asignación de conversaciones (OLA1 GHL #3/#7 1.3d) ──
  // Reusa el dropdown de agentes de la agencia; solo los que tienen cuenta de
  // usuario (userId) son asignables (assignedToId es FK a users, no a agents).
  getAssignableAgents: async (): Promise<AssignableAgent[]> => {
    const { data } = await privateApi.get(`/agency/agents/dropdown?limit=100`);
    const list: AssignableAgent[] = data?.data ?? data ?? [];
    return list.filter((a) => !!a.userId);
  },
};
